import { COMPACT_DIALOG, preventNativeSubmit } from './dialogForm';
import {
  DialogForm,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Icons,
  Input,
} from '@ais-ui/ui-core';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { PiSignIn } from 'react-icons/pi';
import * as z from 'zod';
import { useLoginRequest, type LoginFormValues } from './authApi';
import { useLogin } from './LoginContext';
import { describeLoginError } from './responseErrors';

const FormSchema = z.object({
  username: z.string().min(2, {
    message: 'Username must be at least 2 characters.',
  }),
  password: z.string().min(1, { message: 'Required' }),
});

export function LoginCard() {
  const { login, openDialog, closeDialog } = useLogin();
  const { mutateAsync: requestLogin, isPending } = useLoginRequest();

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: { username: '', password: '' },
  });

  const handleSubmit = async (values: LoginFormValues) => {
    try {
      login(await requestLogin(values));
      form.reset();
    } catch (error) {
      form.setError('root', { message: describeLoginError(error) });
    }
  };

  return (
    <DialogForm
      className={COMPACT_DIALOG.root}
      showDialog={openDialog === 'login'}
      onOpenChange={(open) => !open && closeDialog()}
      size="sm"
    >
      <DialogForm.Header>
        <DialogForm.Title icon={<PiSignIn className="size-5" />}>
          Login to your account
        </DialogForm.Title>
        <DialogForm.Description>
          Enter your account details below to login
        </DialogForm.Description>
      </DialogForm.Header>
      <Form {...form}>
        <DialogForm.Body observeResize={false}>
          <form
            id="oceanio-login-form"
            className="space-y-4"
            noValidate
            onSubmit={preventNativeSubmit}
          >
            <FormField
              control={form.control}
              name="username"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Username (E-mail)</FormLabel>
                  <FormControl onChange={() => form.clearErrors('root')}>
                    <Input
                      type="text"
                      autoComplete="username"
                      placeholder="Username"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Password</FormLabel>
                  <FormControl onChange={() => form.clearErrors('root')}>
                    <Input
                      type="password"
                      autoComplete="current-password"
                      placeholder="Password"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {form.formState.errors.root && (
              <p className="text-sm text-destructive">
                {form.formState.errors.root.message}
              </p>
            )}
          </form>
        </DialogForm.Body>
      </Form>
      <DialogForm.Footer className={COMPACT_DIALOG.footer}>
        <DialogForm.Cancel>Cancel</DialogForm.Cancel>
        <DialogForm.Submit
          id="login-submit"
          form="oceanio-login-form"
          variant="oceanio"
          disabled={isPending}
          onSubmit={form.handleSubmit(handleSubmit)}
        >
          {isPending && <Icons.spinner className="mr-2 size-4 animate-spin" />}
          SIGN IN
        </DialogForm.Submit>
      </DialogForm.Footer>
    </DialogForm>
  );
}
